import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { AuthService } from 'src/app/core/services/auth.service';
import { AuthLayoutComponent } from '../auth-layout/auth-layout.component';

import { SelectModule } from 'primeng/select';
import {
  SocialAuthService,
  SocialUser,
  GoogleSigninButtonModule,
} from '@abacritt/angularx-social-login';
import { Subscription } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { TenantService } from 'src/app/core/services/tenant.service';

// 1. IMPORTAMOS O NOSSO NOVO SERVIÇO DE TOAST
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { MsalService } from '@azure/msal-angular';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    RouterLink,
    AuthLayoutComponent,
    GoogleSigninButtonModule,
    SelectModule,
  ],
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
})
export class LoginPageComponent implements OnInit, OnDestroy {
  model: any = { email: '', password: '' };
  rememberMe = true;
  submitting = false;
  showPassword = false;
  returnUrl = '/home';
  private authSubscription: Subscription;

  showTenantSelector = false;
  selectedTenant: string = '';
  /** Populated from API when running on localhost (dev tenant switcher). */
  availableTenants: { label: string; value: string }[] = [];
  loadingTenants = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private socialAuthService: SocialAuthService,
    public tenantService: TenantService,
    private toastService: AppToastService, // 2. INJETAMOS AQUI
    private msalService: MsalService
  ) { }

  async ngOnInit() {
    this.showTenantSelector = this.tenantService.isLocalhost();
    this.selectedTenant = this.tenantService.tenantId || '';
    this.returnUrl =
      this.route.snapshot.queryParamMap.get('returnUrl') || '/home';

    if (this.showTenantSelector) {
      this.loadTenantsForDevSwitcher();
    }

    await this.msalService.initialize();


    this.authSubscription = this.socialAuthService.authState.subscribe({
      next: (user: SocialUser) => {
        if (user) this.handleGoogleAuth(user);
      },
      error: (err: any) => console.error('Google Auth Error:', err),
    });
  }

  ngOnDestroy() {
    if (this.authSubscription) this.authSubscription.unsubscribe();
  }

  handleGoogleAuth(user: SocialUser) {
    this.authService.googleLogin(user.idToken, this.rememberMe).subscribe({
      next: () => {
        this.toastService.success('Success', 'Welcome back!');
        this.router.navigateByUrl(this.returnUrl);
      },
      error: () => { },
    });
  }

  onSubmit() {
    this.submitting = true;

    this.authService.login(this.model, this.rememberMe).subscribe({
      next: () => {
        this.toastService.success('Success', 'Logged in successfully.');
        this.router.navigateByUrl(this.returnUrl);
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  microsoftLogin() {
    this.msalService.instance.loginPopup({ scopes: ['User.Read'] })
      .then((result) => {
        if (!result?.accessToken) {
          this.toastService.error('Microsoft token was not returned.', 'Error');
          return;
        }

        this.authService.microsoftLogin(result.accessToken, this.rememberMe).subscribe({
          next: () => {
            this.toastService.success('Logged in with Microsoft.', 'Success');
            this.router.navigateByUrl(this.returnUrl);
          },
          error: (err: any) => {
            this.toastService.error(err?.error || err || 'Microsoft login failed.', 'Error');
          },
        });
      })
      .catch((err: any) => {
        this.toastService.error(err?.message || 'Microsoft login failed.', 'Error');
      });
  }

  changeTenant(newTenant: string) {
    this.tenantService.setTenantId(newTenant);

    // Aviso de troca
    this.toastService.warning('Dev Mode', `Changing tenant to ${newTenant}...`);

    setTimeout(() => {
      window.location.reload();
    }, 500);
  }

  private loadTenantsForDevSwitcher(): void {
    this.loadingTenants = true;
    this.tenantService
      .getTenants(500, 0, '')
      .pipe(
        finalize(() => {
          this.loadingTenants = false;
        }),
        catchError((err) => {
          console.error(err);
          this.toastService.error(
            'Could not load tenants from the API.',
            'Dev tenant list',
          );
          return of({ tenants: [] });
        }),
      )
      .subscribe((res: any) => {
        const raw = res?.tenants ?? res?.Tenants ?? [];
        const rows = raw as Array<{
          name?: string;
          Name?: string;
          subdomain?: string;
          Subdomain?: string;
          active?: boolean;
          Active?: boolean;
        }>;

        this.availableTenants = rows
          .filter((t) => {
            const active = t.active ?? t.Active;
            return active !== false;
          })
          .map((t) => ({
            label: (t.name ?? t.Name ?? '').trim() || (t.subdomain ?? t.Subdomain ?? ''),
            value: (t.subdomain ?? t.Subdomain ?? '').trim(),
          }))
          .filter((t) => !!t.value)
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

        const saved = this.tenantService.tenantId || '';
        if (
          saved &&
          this.availableTenants.some((x) => x.value === saved)
        ) {
          this.selectedTenant = saved;
        } else if (this.availableTenants.length) {
          this.selectedTenant = this.availableTenants[0].value;
          this.tenantService.setTenantId(this.selectedTenant);
        }
      });
  }
}
